import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../../components/Layout';
import { StockLevel } from '../../types';
import { stockService } from '../../services/stockService';

const StockReportPage: React.FC = () => {
  const navigate = useNavigate();
  const [lowStockItems, setLowStockItems] = useState<StockLevel[]>([]);
  const [allStockItems, setAllStockItems] = useState<StockLevel[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<'low-stock' | 'all-stock'>('low-stock');
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadReports();
  }, []);

  const loadReports = async () => {
    setLoading(true);
    setError(null);
    try {
      const [lowStockRes, allStockRes] = await Promise.all([
        stockService.getLowStockReport(),
        stockService.getStockReport(undefined, { pageSize: 100 }),
      ]);

      if (lowStockRes.data.data) {
        setLowStockItems(lowStockRes.data.data);
      }

      if (allStockRes.data.data) {
        setAllStockItems(allStockRes.data.data);
      }

      setLoading(false);
    } catch (err) {
      setError('Failed to load stock reports');
      setLoading(false);
    }
  };

  const filteredLowStock = lowStockItems.filter((item) =>
    (item.product?.name.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (item.product?.sku.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (item.warehouse?.name.toLowerCase().includes(searchTerm.toLowerCase()) || false)
  );

  const filteredAllStock = allStockItems.filter((item) =>
    (item.product?.name.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (item.product?.sku.toLowerCase().includes(searchTerm.toLowerCase()) || false) ||
    (item.warehouse?.name.toLowerCase().includes(searchTerm.toLowerCase()) || false)
  );

  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const calculateTotalValue = (items: StockLevel[]) => {
    return items.reduce((total, item) => {
      const unitPrice = item.product?.reorderLevel || 0;
      return total + (item.quantity * unitPrice);
    }, 0);
  };

  const calculateCriticalItems = (items: StockLevel[]) => {
    return items.filter(
      (item) => item.quantity < ((item.product?.reorderLevel || 0) * 0.5)
    ).length;
  };

  if (loading) {
    return (
      <Layout>
        <div className="container-fluid text-center mt-5">
          <div className="spinner-border" role="status">
            <span className="visually-hidden">Loading...</span>
          </div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="container-fluid">
        <div className="d-flex align-items-center mb-4">
          <button
            className="btn btn-link p-0 me-3"
            onClick={() => navigate('/stock/levels')}
          >
            ← Back to Stock
          </button>
          <h1>Stock Reports</h1>
        </div>

        {error && (
          <div className="alert alert-danger alert-dismissible fade show">
            {error}
          </div>
        )}

        <div className="row mb-4">
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title">Low Stock Items</h5>
                <h2 className="text-danger">{lowStockItems.length}</h2>
                <p className="text-muted mb-0">Products below reorder level</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title">Critical Items</h5>
                <h2 className="text-danger">
                  {calculateCriticalItems(lowStockItems)}
                </h2>
                <p className="text-muted mb-0">Below 50% of reorder level</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title">Total SKUs</h5>
                <h2>{allStockItems.length}</h2>
                <p className="text-muted mb-0">All tracked stock items</p>
              </div>
            </div>
          </div>
          <div className="col-md-3">
            <div className="card text-center">
              <div className="card-body">
                <h5 className="card-title">Healthy Stock</h5>
                <h2 className="text-success">
                  {allStockItems.length - lowStockItems.length}
                </h2>
                <p className="text-muted mb-0">At or above reorder level</p>
              </div>
            </div>
          </div>
        </div>

        <div className="card mb-4">
          <div className="card-body">
            <input
              type="text"
              className="form-control"
              placeholder="Search by product name, SKU, or warehouse..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>

        <div className="card">
          <div className="card-header">
            <ul className="nav nav-tabs card-header-tabs" role="tablist">
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'low-stock' ? 'active' : ''}`}
                  onClick={() => setActiveTab('low-stock')}
                >
                  <span className="badge bg-danger me-2">{lowStockItems.length}</span>
                  Low Stock Items
                </button>
              </li>
              <li className="nav-item">
                <button
                  className={`nav-link ${activeTab === 'all-stock' ? 'active' : ''}`}
                  onClick={() => setActiveTab('all-stock')}
                >
                  <span className="badge bg-info me-2">{allStockItems.length}</span>
                  All Stock Items
                </button>
              </li>
            </ul>
          </div>

          <div className="table-responsive">
            <table className="table table-hover mb-0">
              <thead className="table-light">
                <tr>
                  <th>SKU</th>
                  <th>Product Name</th>
                  <th>Warehouse</th>
                  <th className="text-center">Current Qty</th>
                  <th className="text-center">Reorder Level</th>
                  <th className="text-center">Below Level</th>
                  <th className="text-center">Status</th>
                  <th className="text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {activeTab === 'low-stock'
                  ? filteredLowStock.length === 0
                    ? (
                        <tr>
                          <td colSpan={8} className="text-center py-4 text-muted">
                            No low stock items found
                          </td>
                        </tr>
                      )
                    : filteredLowStock.map((item) => (
                        <tr key={item.id} className="table-danger">
                          <td>
                            <code>{item.product?.sku}</code>
                          </td>
                          <td>{item.product?.name}</td>
                          <td>{item.warehouse?.name}</td>
                          <td className="text-center fw-bold">{item.quantity}</td>
                          <td className="text-center">{item.product?.reorderLevel}</td>
                          <td className="text-center">
                            {Math.max(0, (item.product?.reorderLevel || 0) - item.quantity)}
                          </td>
                          <td className="text-center">
                            {item.quantity < ((item.product?.reorderLevel || 0) * 0.5) ? (
                              <span className="badge bg-danger">Critical</span>
                            ) : (
                              <span className="badge bg-warning">Low</span>
                            )}
                          </td>
                          <td className="text-center">
                            <button
                              className="btn btn-sm btn-primary"
                              onClick={() => navigate('/stock/adjust', {
                                state: { productId: item.productId, warehouseId: item.warehouseId },
                              })}
                            >
                              Adjust
                            </button>
                          </td>
                        </tr>
                      ))
                  : filteredAllStock.length === 0
                  ? (
                      <tr>
                        <td colSpan={8} className="text-center py-4 text-muted">
                          No stock items found
                        </td>
                      </tr>
                    )
                  : filteredAllStock.map((item) => (
                      <tr key={item.id}>
                        <td>
                          <code>{item.product?.sku}</code>
                        </td>
                        <td>{item.product?.name}</td>
                        <td>{item.warehouse?.name}</td>
                        <td className="text-center">{item.quantity}</td>
                        <td className="text-center">{item.product?.reorderLevel}</td>
                        <td className="text-center">
                          {item.quantity < (item.product?.reorderLevel || 0)
                            ? Math.max(0, (item.product?.reorderLevel || 0) - item.quantity)
                            : 0}
                        </td>
                        <td className="text-center">
                          {item.quantity < (item.product?.reorderLevel || 0) ? (
                            <span className="badge bg-warning">Low</span>
                          ) : (
                            <span className="badge bg-success">OK</span>
                          )}
                        </td>
                        <td className="text-center">
                          <button
                            className="btn btn-sm btn-outline-secondary"
                            onClick={() => navigate('/stock/movements')}
                          >
                            History
                          </button>
                        </td>
                      </tr>
                    ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="mt-4">
          <button className="btn btn-primary me-2">Export to CSV</button>
          <button className="btn btn-secondary me-2">Export to PDF</button>
          <button className="btn btn-outline-secondary" onClick={loadReports}>
            Refresh
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default StockReportPage;
